import {
  Boxes,
  Calculator,
  Lightbulb,
  MapPin,
  MessageSquare,
  PieChart,
  Radio,
  Receipt,
  ShoppingBag,
  Trash2,
  Truck,
  Users,
  LucideIcon,
} from 'lucide-react';
import { RevenueRange } from './types';

/** Slider stops: the exact counts 1–20 plus a final "20+" stop (21 stops in total). */
export const MAX_EXACT_COUNT = 20;
export const LOCATION_STOP_COUNT = MAX_EXACT_COUNT + 1;
/** Index of the "20+" stop. */
export const TWENTY_PLUS_INDEX = MAX_EXACT_COUNT;

export const REVENUE_RANGES: { id: RevenueRange; labelKey: string }[] = [
  { id: 'lt1m', labelKey: 'bookDemo.revenue.options.lt1m' },
  { id: '1to4m', labelKey: 'bookDemo.revenue.options.1to4m' },
  { id: '4to10m', labelKey: 'bookDemo.revenue.options.4to10m' },
  { id: '10to25m', labelKey: 'bookDemo.revenue.options.10to25m' },
  { id: 'gt25m', labelKey: 'bookDemo.revenue.options.gt25m' },
];

/** The one range that routes to the soft-stop screen instead of a booking (spec §3.4). */
export const BELOW_THRESHOLD_RANGE: RevenueRange = 'lt1m';

/**
 * ⚠️ PLACEHOLDER NUMBERS — invented ranges carried over from the designer mock,
 * NOT validated business figures. The user supplies/approves the real per-tier
 * savings before this page ships (spec §11.2). `lt1m` has no banner: that tier
 * goes to the soft stop.
 */
export const PLACEHOLDER_SAVINGS_BY_REVENUE: Record<RevenueRange, string | null> = {
  lt1m: null,
  '1to4m': '€15,000 – €45,000',
  '4to10m': '€40,000 – €110,000',
  '10to25m': '€90,000 – €250,000',
  gt25m: '€200,000+',
};

export interface ChallengeOption {
  id: string;
  icon: LucideIcon;
}

/** Stable snake_case ids — these are stored in `demo_requests.challenges` server-side. */
export const CHALLENGES: ChallengeOption[] = [
  { id: 'recipe_costing', icon: Calculator },
  { id: 'supplier_prices', icon: Truck },
  { id: 'inventory_counts', icon: Boxes },
  { id: 'multi_location', icon: MapPin },
  { id: 'food_cost_visibility', icon: PieChart },
  { id: 'food_waste', icon: Trash2 },
  { id: 'finance_reporting', icon: Receipt },
  { id: 'purchasing_admin', icon: ShoppingBag },
];

/**
 * The recap's "what we unlock for you" row, ported from the mock. `bestPractice` is the only
 * conditional one: its copy switches on whether they run one location or several.
 */
export const UNLOCK_ITEMS: { id: string; icon: LucideIcon }[] = [
  { id: 'walkthrough', icon: Radio },
  { id: 'solutions', icon: Lightbulb },
  { id: 'bestPractice', icon: Users },
  { id: 'qa', icon: MessageSquare },
];

/** Tiles on the recap's "we'll focus on" card — the mock always shows three. */
export const MAX_FOCUS_ITEMS = 3;
/** The mock badges the first two tiles High and the third Medium. */
export const HIGH_PRIORITY_ITEMS = 2;

/** Pad order for the focus card when fewer than 3 were selected — most broadly applicable first. */
export const FOCUS_PAD_ORDER: string[] = [
  'food_cost_visibility',
  'recipe_costing',
  'supplier_prices',
  'purchasing_admin',
  'inventory_counts',
  'food_waste',
  'finance_reporting',
  'multi_location',
];

/** Their picks in the order chosen, topped up from FOCUS_PAD_ORDER so the card always has 3 tiles. */
export const resolveFocusChallenges = (selected: string[]): string[] => {
  const picked = selected.slice(0, MAX_FOCUS_ITEMS);
  for (const id of FOCUS_PAD_ORDER) {
    if (picked.length >= MAX_FOCUS_ITEMS) break;
    if (!picked.includes(id)) picked.push(id);
  }
  return picked;
};

/** Same shape the profile pages use; the server validates properly on submit. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string) => EMAIL_PATTERN.test(email.trim());
